import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SectionHeader from '../ui/SectionHeader';

const Section = styled.section`
  padding: 96px 0;
  background: #f7fafc;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    max-width: 440px;
    margin: 0 auto;
  }
`;

const Card = styled(motion.div)`
  padding: 28px 24px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8eef3;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const QuoteIcon = styled.i`
  color: #0e6b3c;
  font-size: 1.1rem;
  margin-bottom: 14px;
  opacity: 0.5;
`;

const Quote = styled.p`
  font-size: 0.9rem;
  line-height: 1.7;
  color: #64748b;
  font-style: italic;
  flex: 1;
  margin-bottom: 20px;
`;

const Stars = styled.div`
  display: flex;
  gap: 3px;
  margin-bottom: 16px;

  i {
    color: #f2b705;
    font-size: 0.75rem;
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0e6b3c, #6dbb1a);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  h4 {
    font-family: var(--font-display);
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
  }

  span {
    font-size: 0.78rem;
    color: #94a3b8;
  }
`;

const testimonials = [
  { content: 'Rhutech delivered an exceptional e-commerce platform. Their technical expertise is outstanding.', author: 'Aman Varma', position: 'CEO, Namo Stones', initial: 'A' },
  { content: 'The school management system revolutionized our admin. Highly professional with excellent communication.', author: 'Dr. Rajesh Kumar', position: 'Principal, Modern High School', initial: 'R' },
  { content: 'Digital marketing strategies increased our online presence by 300%. Our go-to tech partner.', author: 'Priya Sharma', position: 'Marketing Director', initial: 'P' },
];

const TestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Section ref={ref} id="testimonials">
      <div className="container">
        <SectionHeader label="Testimonials" title="What Our Clients Say" />
        <Grid>
          {testimonials.map((t, i) => (
            <Card
              key={t.author}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
            >
              <QuoteIcon className="fas fa-quote-left" />
              <Quote>"{t.content}"</Quote>
              <Stars>{[...Array(5)].map((_, j) => <i key={j} className="fas fa-star" />)}</Stars>
              <Author>
                <Avatar>{t.initial}</Avatar>
                <AuthorInfo>
                  <h4>{t.author}</h4>
                  <span>{t.position}</span>
                </AuthorInfo>
              </Author>
            </Card>
          ))}
        </Grid>
      </div>
    </Section>
  );
};

export default TestimonialsSection;
